import { message } from "./messages"
import { JSONUIProvider, Renderer } from "@json-render/react"
import { useMemo, useRef, useState } from "react"
import type { NornEvaluate } from "./conditions"
import { NavigationView, UnavailableBlock, catalog, registry } from "./catalog"
import { nodeIndex, projectPage } from "./project"
import { type OpenForm, RuntimeProvider, buildMaps, useBindings, useEvaluator, usePrincipal, type WidgetHost, type WidgetScope } from "./runtime"
import { pageBindings, type Ux, type UxPage, type WidgetDefinitionSet } from "./model"

export interface WidgetPageViewProps {
  readonly appId: string
  readonly ux: Ux
  readonly page: UxPage
  readonly set: WidgetDefinitionSet
  readonly scope: WidgetScope
  readonly context: Readonly<Record<string, unknown>>
  readonly locale: string
  readonly host: WidgetHost
  readonly loadEvaluator: (locale?: string) => Promise<NornEvaluate>
}

/** One admitted WidgetIR page view shared by Admin and MCP Apps hosts. */
export function WidgetPageView({ appId, ux, page, set, scope, context, locale, host, loadEvaluator }: WidgetPageViewProps) {
  const spec = useMemo(() => projectPage(page, ux, locale), [page, ux, locale])
  const validation = useMemo(() => catalog.validate(spec), [spec])
  const nodes = useMemo(() => nodeIndex(page), [page])
  const maps = useMemo(() => buildMaps(ux), [ux])
  const selectable = useMemo(() => new Set([...nodes.values()].flatMap((node) => node.component.kind === "list" && node.component.selection === "single" ? [node.component.dataBindingId] : [])), [nodes])
  const [selected, setSelected] = useState<ReadonlyMap<string, readonly string[]>>(new Map())
  const { states: bindings, loadMore } = useBindings(scope, appId, set, page, ux, pageBindings(page, ux), context, host, { selectable, selected }, locale)
  const evaluator = useEvaluator(loadEvaluator, locale)
  const principal = usePrincipal(scope, host)
  const [opened, setOpened] = useState<Map<string, OpenForm>>(new Map())
  const openSession = useRef(0)
  const applicationNavigation = ux.app?.navigationItemIds ?? []
  if (!validation.success) {
    return <UnavailableBlock why={{ reason: message(locale, "page.invalidProjection", { error: validation.error?.issues.map((issue) => `${issue.path.map(String).join(".")}: ${issue.message}`).join("; ") ?? message(locale, "page.invalidSpec") }) }} />
  }
  return (
    <RuntimeProvider
      value={{
        scope,
        set,
        ux,
        appId,
        page,
        nodes,
        bindings,
        loadMore,
        context,
        locale,
        ...maps,
        openedForms: opened,
        openForm: (form) => {
          openSession.current += 1
          const next = { ...form, sessionId: openSession.current }
          setOpened((current) => new Map(current).set(form.formId, next))
        },
        closeForm: (formId) => setOpened((current) => { const next = new Map(current); next.delete(formId); return next }),
        navigate: host.navigate,
        explain: host.explain,
        selections: selected,
        select: (bindingId, keys) => setSelected((current) => new Map(current).set(bindingId, keys)),
        evaluator,
        principal,
        host,
      }}
    >
      <JSONUIProvider registry={registry}>
        <div className="grid gap-6">
          {applicationNavigation.length > 0 ? (
            <nav aria-label={message(locale, "navigation.applicationPages")} className="flex max-h-40 flex-wrap items-center gap-1 overflow-y-auto border-b border-border-subtle pb-3">
              {applicationNavigation.map((itemId) => <NavigationView key={itemId} itemId={itemId} application />)}
            </nav>
          ) : null}
          <Renderer spec={spec} registry={registry} fallback={({ element }) => <UnavailableBlock why={{ reason: message(locale, "page.unsupportedComponent", { type: element.type }) }} />} />
        </div>
      </JSONUIProvider>
    </RuntimeProvider>
  )
}
